// Метаданные типов инструмента — UI-side каталог по `ToolKind`-вариантам
// из Rust-крейта `tool_kind` (`crates/lib/tool_kind/`). Поля и дефолты
// синхронизированы вручную с `tool_library::Tool::new_for_edit` и
// `kinds/<variant>::dummy()`. При расхождении wire-формата с Rust серверный
// `serde_json::from_value::<Tool>` упадёт — это будет видно сразу.
//
// payload — это externally-tagged enum `Tool` (`{"PdcBit": {...}}`,
// `{"DrillPipe": {...}}` и т.д., см. crates/lib/tool_library/src/tool.rs).
// Все unit-newtypes в Rust имеют `#[serde(transparent)]` → в JSON это
// плоские числа.
//
// Размеры в форме показываем в **миллиметрах** (диаметры) и **метрах**
// (длины), чтобы соответствовать привычной нотации полевиков. На wire'е
// Rust ждёт метры (UDiameterMeters / ULenghtMeters) — конверсия в editor'е.
// Mass: tube-with-joins → kg/m, всё остальное → kg.

import type { ToolKind } from './tool_kind'

/// Группы по shape — определяют какой editor показывать. Tube-with-joins
/// и tube-simple отличаются полем mass (kg/m vs kg) и наличием подформы joins.
export type ToolShape = 'tube_with_joins' | 'tube_simple' | 'bit' | 'reamer'

/// Единица измерения для отображения. На wire'е всё в SI; конверсия — в редакторе.
export type FieldUnit = 'mm' | 'm' | 'kg' | 'kg_per_m' | 'mpa' | 'knm' | 'kpa' | 'count' | 'unitless'

export type ToolField = {
  /// Ключ в payload (имя поля в Rust). Для вложенных — dot-path: 'joins.outer_end_D2'.
  key:   string
  /// Подпись в форме.
  label: string
  /// Поясняющий текст.
  hint?: string
  unit:  FieldUnit
}

export type ToolKindMeta = {
  /// Идентификатор kind на бэке. Зеркало Rust `ToolKind::wire_id()`.
  id:    ToolKind
  /// Тег varianta в payload (внешне-тегированный enum). Для PdcBit это 'PdcBit',
  /// для DrillPipe — 'DrillPipe', и т.д. Не совпадает с wire-id.
  tag:   string
  shape: ToolShape
  /// Короткий код для кнопки выбора типа.
  code:  string
  /// Человеко-читаемое имя.
  label: string
  /// PNG-чертеж (наследие от equipment).
  asset: string
  /// Поля основного тела инструмента (без вложенных подформ joins/nozzles —
  /// они добавляются отдельно в editor'е по shape).
  fields: ToolField[]
}

// Общие наборы полей по shape — чтобы не дублировать текстом 6 раз.

const FIELDS_TUBE_WITH_JOINS: ToolField[] = [
  { key: 'diam_outer',          label: 'D',   unit: 'mm',       hint: 'наружный диаметр' },
  { key: 'diam_wall_thickness', label: 'δ',   unit: 'mm',       hint: 'толщина стенки' },
  { key: 'lenght_overall',      label: 'L',   unit: 'm',        hint: 'длина одной секции' },
  { key: 'mass',                label: 'm',   unit: 'kg_per_m', hint: 'погонная масса' },
  { key: 'eccentricity',        label: 'ε',   unit: 'unitless', hint: 'эксцентриситет' },
  { key: 'yield_strength_mpa',  label: 'σт',  unit: 'mpa',      hint: 'предел текучести тела' },
  { key: 'yield_strength_tj_mpa', label: 'σт.tj', unit: 'mpa',  hint: 'предел текучести замка' },
  { key: 'mut_knm',             label: 'MUT', unit: 'knm',      hint: 'момент свинчивания' },
  { key: 'sections_count',      label: 'N',   unit: 'count',    hint: 'число секций' },
]

const FIELDS_TUBE_SIMPLE: ToolField[] = [
  { key: 'diam_outer',         label: 'D',   unit: 'mm',       hint: 'наружный диаметр' },
  { key: 'diam_inner',         label: 'd',   unit: 'mm',       hint: 'внутренний диаметр' },
  { key: 'lenght',             label: 'L',   unit: 'm',        hint: 'длина' },
  { key: 'pressure_delta',     label: 'ΔP',  unit: 'kpa',      hint: 'перепад давления' },
  { key: 'mass',               label: 'm',   unit: 'kg',       hint: 'масса' },
  { key: 'eccentricity',       label: 'ε',   unit: 'unitless', hint: 'эксцентриситет' },
  { key: 'yield_strength_mpa', label: 'σт',  unit: 'mpa',      hint: 'предел текучести' },
  { key: 'mut_knm',            label: 'MUT', unit: 'knm',      hint: 'момент свинчивания' },
]

const FIELDS_BIT: ToolField[] = [
  { key: 'D',                  label: 'D',   unit: 'mm',  hint: 'диаметр долота' },
  { key: 'd',                  label: 'd',   unit: 'mm',  hint: 'диаметр канала' },
  { key: 'L',                  label: 'L',   unit: 'm',   hint: 'длина' },
  { key: 'M',                  label: 'M',   unit: 'kg',  hint: 'масса' },
  { key: 'yield_strength_mpa', label: 'σт',  unit: 'mpa', hint: 'предел текучести' },
  { key: 'mut_knm',            label: 'MUT', unit: 'knm', hint: 'момент свинчивания' },
]

const FIELDS_REAMER: ToolField[] = [
  { key: 'D1',                 label: 'D1',  unit: 'mm',  hint: 'наибольший расширительный диаметр' },
  { key: 'Dl',                 label: 'Dl',  unit: 'mm',  hint: 'диаметр лопасти' },
  { key: 'D2',                 label: 'D2',  unit: 'mm' },
  { key: 'D3',                 label: 'D3',  unit: 'mm' },
  { key: 'd',                  label: 'd',   unit: 'mm',  hint: 'диаметр канала' },
  { key: 'l1',                 label: 'l1',  unit: 'm' },
  { key: 'll',                 label: 'll',  unit: 'm' },
  { key: 'l2',                 label: 'l2',  unit: 'm' },
  { key: 'l3',                 label: 'l3',  unit: 'm' },
  { key: 'L',                  label: 'L',   unit: 'm',   hint: 'общая длина' },
  { key: 'M',                  label: 'M',   unit: 'm',   hint: 'mass-как-длина (см. tdrs)' },
  { key: 'delta_P',            label: 'ΔP',  unit: 'kpa', hint: 'перепад давления' },
  { key: 'yield_strength_mpa', label: 'σт',  unit: 'mpa' },
  { key: 'mut_knm',            label: 'MUT', unit: 'knm' },
]

export const TOOL_KINDS: ToolKindMeta[] = [
  { id: 'pdc_bit',     tag: 'PdcBit',     shape: 'bit',             code: 'PDC', label: 'PDC долото',           asset: '/equipment/pdc-bit.png',     fields: FIELDS_BIT },
  { id: 'tricone_bit', tag: 'TriconeBit', shape: 'bit',             code: 'III', label: 'Шарошечное долото',    asset: '/equipment/tricone-bit.png', fields: FIELDS_BIT },
  { id: 'dp',          tag: 'DrillPipe',  shape: 'tube_with_joins', code: 'DP',  label: 'Бурильная труба (DP)', asset: '/equipment/dp.png',          fields: FIELDS_TUBE_WITH_JOINS },
  { id: 'dc',          tag: 'DrillCollar',shape: 'tube_with_joins', code: 'DC',  label: 'УБТ (DC)',             asset: '/equipment/dc.png',          fields: FIELDS_TUBE_WITH_JOINS },
  { id: 'hmdc',        tag: 'Hmdc',       shape: 'tube_with_joins', code: 'NM',  label: 'НУБТ (HMDC)',          asset: '/equipment/hmdc.png',        fields: FIELDS_TUBE_WITH_JOINS },
  { id: 'hwdp1',       tag: 'Hwdp1',      shape: 'tube_with_joins', code: 'H1',  label: 'ТБТ (HWDP 1)',         asset: '/equipment/hwdp1.png',       fields: FIELDS_TUBE_WITH_JOINS },
  { id: 'hwdp2',       tag: 'Hwdp2',      shape: 'tube_with_joins', code: 'H2',  label: 'ТБТ (HWDP 2)',         asset: '/equipment/hwdp2.png',       fields: FIELDS_TUBE_WITH_JOINS },
  { id: 'casing',      tag: 'Casing',     shape: 'tube_with_joins', code: 'CSG', label: 'Обсадная (Casing)',    asset: '/equipment/casing.png',      fields: FIELDS_TUBE_WITH_JOINS },
  { id: 'stabilizer',  tag: 'Stabilizer', shape: 'tube_simple',     code: 'KLS', label: 'Калибратор (KLS)',     asset: '/equipment/stabilizer.png',  fields: FIELDS_TUBE_SIMPLE },
  { id: 'pdm',         tag: 'Pdm',        shape: 'tube_simple',     code: 'PDM', label: 'ВЗД (PDM)',            asset: '/equipment/pdm.png',         fields: FIELDS_TUBE_SIMPLE },
  { id: 'reamer',      tag: 'Reamer',     shape: 'reamer',          code: 'RM',  label: 'Расширитель (Reamer)', asset: '/equipment/reamer.png',      fields: FIELDS_REAMER },
  { id: 'tj',          tag: 'Tj',         shape: 'tube_simple',     code: 'TJ',  label: 'TJ',                   asset: '/equipment/tj.png',          fields: FIELDS_TUBE_SIMPLE },
  { id: 'mwd',         tag: 'Mwd',        shape: 'tube_simple',     code: 'MWD', label: 'MWD',                  asset: '/equipment/dp.png',          fields: FIELDS_TUBE_SIMPLE },
  { id: 'jar',         tag: 'Jar',        shape: 'tube_simple',     code: 'JAR', label: 'Ясс (Jar)',            asset: '/equipment/dp.png',          fields: FIELDS_TUBE_SIMPLE },
  { id: 'crossover',   tag: 'Crossover',  shape: 'tube_simple',     code: 'XO',  label: 'Переводник (XO)',      asset: '/equipment/dp.png',          fields: FIELDS_TUBE_SIMPLE },
]

export const KIND_BY_ID: Record<ToolKind, ToolKindMeta> = Object.fromEntries(
  TOOL_KINDS.map((k) => [k.id, k]),
) as Record<ToolKind, ToolKindMeta>

export function kindLabel(id: ToolKind): string {
  return KIND_BY_ID[id]?.label ?? id
}

// ───────────────────────── unit conversion ─────────────────────────

/// SI → display: метры → мм, и т.п. На wire'е всё в SI, в форме — в привычных
/// для домена единицах. Возврат 0 для NaN/невалидных, чтобы инпуты были стабильны.
export function fromSi(value: number, unit: FieldUnit): number {
  if (!Number.isFinite(value)) return 0
  switch (unit) {
    case 'mm':       return value * 1000.0  // m → mm
    case 'm':        return value
    case 'kg':       return value
    case 'kg_per_m': return value
    case 'mpa':      return value
    case 'knm':      return value
    case 'kpa':      return value
    case 'count':    return value
    case 'unitless': return value
  }
}

/// Display → SI. Зеркало fromSi.
export function toSi(displayValue: number, unit: FieldUnit): number {
  if (!Number.isFinite(displayValue)) return 0
  switch (unit) {
    case 'mm':       return displayValue / 1000.0
    case 'm':        return displayValue
    case 'kg':       return displayValue
    case 'kg_per_m': return displayValue
    case 'mpa':      return displayValue
    case 'knm':      return displayValue
    case 'kpa':      return displayValue
    case 'count':    return Math.round(displayValue)
    case 'unitless': return displayValue
  }
}

// ───────────────────────── scaffolding ─────────────────────────

/// Дефолтные joins для tube-with-joins — TS зеркало `Joins::dummy()` из
/// `crates/lib/tool_library/src/joins.rs`. 12mm/1m по всем сегментам.
function dummyJoins(): Record<string, number> {
  return {
    outer_end_l2:       1.0,
    outer_end_D2:       0.012,
    outer_end_mid_l4:   1.0,
    outer_end_mid_D4:   0.012,
    outer_start_mid_l3: 1.0,
    outer_start_mid_D3: 0.012,
    outer_start_l1:     1.0,
    outer_start_D1:     0.012,
    inner_end_l6:       1.0,
    inner_end_D6:       0.012,
    inner_start_l5:     1.0,
    inner_start_D5:     0.012,
  }
}

/// Сгенерировать новый `ToolId` (uuid v4) — для поля `id` внутри payload.
/// В отличие от `tool.id` (внешний uuid каталога), ToolId внутри payload
/// нужен для ссылочной целостности FEA-расчёта; генерим клиентом, бэк
/// сохраняет «как есть».
function newToolUuid(): string {
  // crypto.randomUUID доступен в современных браузерах и в bun. Если
  // среда без него — fallback на простой v4.
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  // Fallback v4: 16 случайных байт + битовая маска версии/варианта.
  const b = new Uint8Array(16)
  for (let i = 0; i < 16; i++) b[i] = Math.floor(Math.random() * 256)
  b[6] = (b[6] & 0x0f) | 0x40
  b[8] = (b[8] & 0x3f) | 0x80
  const h = Array.from(b, (x) => x.toString(16).padStart(2, '0'))
  return `${h.slice(0, 4).join('')}-${h.slice(4, 6).join('')}-${h.slice(6, 8).join('')}-${h.slice(8, 10).join('')}-${h.slice(10, 16).join('')}`
}

/// Скаффолд дефолтного payload для нового tool — TS-зеркало
/// `Tool::new_for_edit(ToolId::new_v4(), kind)`. Возвращает уже обёрнутый
/// в externally-tagged enum (`{<Tag>: {...}}`), готовый к POST.
///
/// Дефолты подобраны под `<Variant>::dummy()` из `crates/lib/tool_library/src/kinds/`.
/// При расхождении сервер вернёт 400 (validate_payload) или storage упадёт
/// при `serde_json::from_value::<Tool>` — оба случая видны сразу.
export function scaffoldPayload(kind: ToolKind): Record<string, unknown> {
  const meta = KIND_BY_ID[kind]
  const id = newToolUuid()
  const description = ''

  let inner: Record<string, unknown>
  switch (meta.shape) {
    case 'tube_with_joins':
      // mm: diam_outer=80, diam_wall_thickness=50; m: lenght_overall=1; kg/m: mass=40.
      inner = {
        id, description,
        diam_outer:            0.080,
        diam_wall_thickness:   0.050,
        lenght_overall:        1.0,
        mass:                  40.0,
        eccentricity:          1.0,
        yield_strength_mpa:    0.0,
        yield_strength_tj_mpa: 0.0,
        mut_knm:               0.0,
        joins:                 dummyJoins(),
        sections_count:        1,
      }
      break

    case 'tube_simple':
      // mm: diam_outer=80, diam_inner=50; m: lenght=1; kg: mass=40.
      inner = {
        id, description,
        diam_outer:         0.080,
        diam_inner:         0.050,
        lenght:             1.0,
        pressure_delta:     0.0,
        mass:               40.0,
        eccentricity:       1.0,
        yield_strength_mpa: 0.0,
        mut_knm:            0.0,
      }
      break

    case 'bit':
      // mm: D=215.9, d=40; m: L=0.4; kg: M=50; nozzles=[].
      inner = {
        id, description,
        d:                  0.040,
        D:                  0.2159,
        L:                  0.4,
        M:                  50.0,
        nozzles:            { items: [] },
        yield_strength_mpa: 0.0,
        mut_knm:            0.0,
      }
      break

    case 'reamer':
      // Зеркало `Reamer::dummy()` из crates/lib/tool_library/src/kinds/reamer.rs.
      inner = {
        id, description,
        D1: 0.244, Dl: 0.244, D2: 0.212, D3: 0.212, d: 0.070,
        l1: 0.4,   ll: 0.4,   l2: 0.4,   l3: 0.4,   L:  0.4, M: 0.4,
        delta_P:            100.0,
        yield_strength_mpa: 0.0,
        mut_knm:            0.0,
      }
      break
  }

  return { [meta.tag]: inner }
}

/// Извлечь body инструмента из payload (распаковать externally-tagged enum).
/// Возвращает `null` если payload пустой или тег не совпадает с kind.
export function unwrapPayload(kind: ToolKind, payload: Record<string, unknown>): Record<string, unknown> | null {
  const meta = KIND_BY_ID[kind]
  if (!meta) return null
  const inner = payload[meta.tag]
  if (inner == null || typeof inner !== 'object') return null
  return inner as Record<string, unknown>
}

/// Обернуть body обратно в externally-tagged enum.
export function wrapPayload(kind: ToolKind, body: Record<string, unknown>): Record<string, unknown> {
  const meta = KIND_BY_ID[kind]
  return { [meta.tag]: body }
}

// ───────────────────────── geometry helpers ─────────────────────────

/// Внешний диаметр тела инструмента в метрах (SI). Зеркало `Tool::body_outer_diam`
/// из `crates/lib/tool_library/src/tool.rs`. Возвращает 0 если payload пустой
/// или у kind нет соответствующего поля.
export function bodyOuterDiamM(kind: ToolKind, payload: Record<string, unknown>): number {
  const body = unwrapPayload(kind, payload)
  if (!body) return 0
  switch (kind) {
    case 'pdc_bit':
    case 'tricone_bit':
      return numField(body, 'D')
    case 'reamer':
      return numField(body, 'D1')
    case 'dp':
    case 'dc':
    case 'hmdc':
    case 'hwdp1':
    case 'hwdp2':
    case 'casing':
    case 'stabilizer':
    case 'pdm':
    case 'mwd':
    case 'jar':
    case 'crossover':
    case 'tj':
      return numField(body, 'diam_outer')
  }
}

/// Внутренний диаметр тела (канал) в метрах. Для tube-with-joins вычисляется
/// `diam_outer - 2 * diam_wall_thickness` (нет явного inner). Зеркало
/// `Tool::body_inner_diam`.
export function bodyInnerDiamM(kind: ToolKind, payload: Record<string, unknown>): number {
  const body = unwrapPayload(kind, payload)
  if (!body) return 0
  switch (kind) {
    case 'pdc_bit':
    case 'tricone_bit':
      return numField(body, 'd')
    case 'reamer':
      return numField(body, 'd')
    case 'dp':
    case 'dc':
    case 'hmdc':
    case 'hwdp1':
    case 'hwdp2':
    case 'casing':
      return Math.max(0, numField(body, 'diam_outer') - 2 * numField(body, 'diam_wall_thickness'))
    case 'stabilizer':
    case 'pdm':
    case 'mwd':
    case 'jar':
    case 'crossover':
    case 'tj':
      return numField(body, 'diam_inner')
  }
}

function numField(body: Record<string, unknown>, key: string): number {
  const v = body[key]
  return typeof v === 'number' && Number.isFinite(v) ? v : 0
}
